import { faChevronDown, faChevronRight, faLock } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { useState, type ReactNode } from "react";
import { RulesText } from "../../RulesText";
import { useRangeHover } from "../rangePreview";
import { NPC_FEATURE_GM_LABEL, NPC_FEATURE_GM_NOTE } from "../npcFeatures";
import type { LoadoutGroup, LoadoutRow } from "../unitLoadout";
import type { Ref } from "../../../routes/compendium/compendiumData";
import type { PreselectBonus } from "../../../routes/home/DiceApp";
import { StatRollButton } from "./unitStats";
import "./unit-sheet.css";

/**
 * The unit sheet's **loadout** (`unit-sheet-redesign` Phase 4): the groups {@link mechLoadout} or
 * {@link npcLoadout} derive, one row per weapon, system or feature, and the REACTIONS group.
 *
 * A row with rule text is a button: a click opens its text inline, and each row toggles on its own.
 * Rows are read, never acted on; the command menu acts. The one exception is the REACTIONS group,
 * whose Overwatch and Brace rows stay clickable until the reactions-in-menu follow-up (Decision 2).
 * The host passes that group's clickable column as `reactions`.
 *
 * From 600px the reactions take a second column, but only when there are some. Below that, or
 * without reactions, it is one column. The width is the window body's (the `.unit-sheet` container),
 * never the viewport's.
 *
 * `locked` is a masked hostile's locked row (`3 FEATURES NOT SEEN · HACK › SCAN REVEALS`), last in
 * the main column: the loadout's rows the viewer has not seen, as a count with no names.
 */
export function UnitLoadout({
  groups,
  reactions,
  locked,
  onOpenRef,
  onOpenDice,
  rowActions,
  sectionActions,
  groupFoot,
  rowClass,
}: {
  groups: readonly LoadoutGroup[];
  reactions?: ReactNode;
  locked?: string;
  /** Open a row's COMPENDIUM record (the BUILD pages): an open row's rule text gains the link, and a row with no text opens it directly. */
  onOpenRef?: (ref: Ref) => void;
  /** Seed DICE from a rollable row (a skill trigger); `undefined` shows no roll buttons. */
  onOpenDice?: (preselect: PreselectBonus) => void;
  /** BUILD in EDIT mode: a row's controls (SWAP, ✕, ＋ EQUIP), at its right end. */
  rowActions?: (row: LoadoutRow, group: LoadoutGroup) => ReactNode;
  /** BUILD in EDIT mode: a mount label's controls (RETROFIT); `row` is the first row under it. */
  sectionActions?: (section: string, row: LoadoutRow) => ReactNode;
  /** BUILD in EDIT mode: a control under a group's rows (＋ INSTALL SYSTEM). */
  groupFoot?: (group: LoadoutGroup) => ReactNode;
  /** BUILD in EDIT mode: a row's extra class (PILOT's level-up diff, `is-gained`). */
  rowClass?: (row: LoadoutRow, group: LoadoutGroup) => string | undefined;
}) {
  if (groups.length === 0 && !reactions && !locked) return null;
  return (
    <div className={`unit-sheet-loadout${reactions && groups.length > 0 ? " has-reactions" : ""}`}>
      {(groups.length > 0 || locked) && (
        <div className="unit-sheet-loadout-main">
          {groups.map((g) => (
            <section key={g.id} className="unit-sheet-lgroup" aria-label={g.title.toLowerCase()} data-build-anchor={g.anchor}>
              <div className={`unit-sheet-lgroup-head${g.flag ? ` is-flag-${g.flag.severity}` : ""}`} title={g.flag?.title}>
                <span className="unit-sheet-lgroup-title">{g.title}</span>
                {g.meter ? (
                  <GroupMeter meter={g.meter} />
                ) : (
                  g.rows.some((r) => r.text) && <span className="unit-sheet-lgroup-hint">CLICK A ROW FOR ITS RULE</span>
                )}
              </div>
              {g.rows.length === 0 && g.empty ? (
                <div className="unit-sheet-lgroup-empty">{g.empty}</div>
              ) : (
                <ul className="unit-sheet-lrows">
                  {g.rows.map((r, i) => {
                    const section = r.section !== undefined && r.section !== g.rows[i - 1]?.section ? r.section : undefined;
                    return (
                      <LoadoutRowItem
                        key={r.key}
                        row={r}
                        section={section}
                        sectionActions={section !== undefined ? sectionActions?.(section, r) : undefined}
                        actions={rowActions?.(r, g)}
                        extraClass={rowClass?.(r, g)}
                        onOpenRef={onOpenRef}
                        onOpenDice={onOpenDice}
                      />
                    );
                  })}
                </ul>
              )}
              {groupFoot?.(g)}
            </section>
          ))}
          {locked && (
            <div className="unit-sheet-lrow-locked">
              <Icon icon={faLock} className="icon-lead" />
              {locked}
            </div>
          )}
        </div>
      )}
      {reactions && <div className="unit-sheet-loadout-side">{reactions}</div>}
    </div>
  );
}

/** The read-only rows under the REACTIONS header: an NPC's reaction features (Superhot), with `featureState`'s pills. */
export function LoadoutRows({ rows }: { rows: readonly LoadoutRow[] }) {
  if (rows.length === 0) return null;
  return (
    <ul className="unit-sheet-lrows">
      {rows.map((r) => (
        <LoadoutRowItem key={r.key} row={r} />
      ))}
    </ul>
  );
}

/** A budget in a group's head: `SP 6 / 6` over a bar that fills toward the max and takes the accent once over it. */
function GroupMeter({ meter }: { meter: NonNullable<LoadoutGroup["meter"]> }) {
  const pct = meter.max > 0 ? Math.min(100, (meter.used / meter.max) * 100) : 0;
  return (
    <span className={`unit-sheet-meter${meter.used > meter.max ? " is-over" : ""}`} aria-label={`${meter.label} ${meter.used} of ${meter.max}`}>
      <span className="unit-sheet-meter-num">
        {meter.label} {meter.used} <small>/ {meter.max}</small>
      </span>
      <span className="unit-sheet-meter-bar" aria-hidden>
        <span className="unit-sheet-meter-fill" style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
}

function LoadoutRowItem({
  row: r,
  section,
  sectionActions,
  actions,
  extraClass,
  onOpenRef,
  onOpenDice,
}: {
  row: LoadoutRow;
  extraClass?: string;
  /** The mount label this row opens, when it starts a new one. */
  section?: string;
  sectionActions?: ReactNode;
  actions?: ReactNode;
  onOpenRef?: (ref: Ref) => void;
  onOpenDice?: (preselect: PreselectBonus) => void;
}) {
  const [open, setOpen] = useState(false);
  // A weapon, or a system or feature with a reach, paints it on the board while hovered (range-hover).
  const rangeHover = useRangeHover(r.preview);
  const ref = r.ref;
  const opensRecord = !r.text && ref !== undefined && onOpenRef !== undefined;
  const head = (
    <>
      <span className="unit-sheet-lrow-name">
        <span className="unit-sheet-lrow-caret" aria-hidden>
          {r.text ? <Icon icon={open ? faChevronDown : faChevronRight} /> : null}
        </span>
        <span className={`unit-sheet-kind is-${r.kind.toLowerCase()}`}>{r.kind}</span>
        <span className="unit-sheet-lrow-label">{r.name}</span>
        {r.gmRuled && (
          <span className="unit-sheet-lrow-gm" title={NPC_FEATURE_GM_NOTE}>
            {NPC_FEATURE_GM_LABEL}
          </span>
        )}
      </span>
      {r.meta && <span className="unit-sheet-lrow-meta">{r.meta}</span>}
      {r.chips.length > 0 && (
        <span className="unit-sheet-lrow-chips">
          {r.chips.map((c, i) => (
            <span key={`${i}:${c.label}`} className={c.tone === "tag" ? "unit-sheet-ltag" : `unit-sheet-pill is-${c.tone}`} title={c.title}>
              {c.label}
            </span>
          ))}
        </span>
      )}
    </>
  );
  return (
    <>
      {section && (
        <li className="unit-sheet-lsection">
          {section}
          {sectionActions}
        </li>
      )}
      <li
        className={`unit-sheet-lrow${r.destroyed ? " is-destroyed" : ""}${open ? " is-open" : ""}${r.variant ? ` is-${r.variant}` : ""}${r.roll && onOpenDice ? " has-roll" : ""}${actions ? " has-edit" : ""}${extraClass ? ` ${extraClass}` : ""}`}
        data-row={r.key}
        {...rangeHover}
      >
        {r.text ? (
          <button type="button" className="unit-sheet-lrow-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {head}
          </button>
        ) : opensRecord ? (
          <button type="button" className="unit-sheet-lrow-head" onClick={() => onOpenRef(ref)}>
            {head}
          </button>
        ) : (
          <div className="unit-sheet-lrow-head">{head}</div>
        )}
        {r.roll && onOpenDice && <StatRollButton label={r.roll.label} bonus={r.roll.bonus} onOpenDice={onOpenDice} />}
        {actions && <span className="unit-sheet-lrow-edit">{actions}</span>}
        {open && r.text && (
          <div className="unit-sheet-lrow-body">
            <RulesText className="unit-sheet-lrow-text" html={r.text} />
            {ref && onOpenRef && (
              <button type="button" className="unit-sheet-lrow-ref" onClick={() => onOpenRef(ref)}>
                OPEN IN COMPENDIUM ›
              </button>
            )}
          </div>
        )}
      </li>
    </>
  );
}
